import { getUsStateCodeByName, isUsStateCode } from '@/shared/lib/usStates';
import { SUPPORTED_PRACTICE_AREAS, toSupportedPracticeArea } from '@/shared/lib/practiceAreas';

export interface FillEmployeeRecordFormErrors {
  displayName: string | null;
  email: string | null;
  licensedStates: string | null;
  licensedPracticeAreas: string | null;
}

export interface FillEmployeeRecordValidatedValues {
  licensedStates: string[];
  licensedPracticeAreas: string[];
}

export function normalizeCommaSeparatedList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item !== '');
}

export function normalizeStateCodes(value: string): string[] {
  return normalizeStateSelections(normalizeCommaSeparatedList(value));
}

export function normalizeStateSelections(values: string[]): string[] {
  const normalized = values
    .map((item) => item.trim())
    .filter((item) => item !== '')
    .map((item) => {
      if (isUsStateCode(item)) {
        return item.toUpperCase();
      }

      return getUsStateCodeByName(item) ?? item.toUpperCase();
    });
  return Array.from(new Set(normalized));
}

function validateDisplayName(value: string): string | null {
  return value.trim() === '' ? 'Full name is required.' : null;
}

function validateEmail(value: string): string | null {
  return value.trim() === '' ? 'Email is required.' : null;
}

function validateLicensedStates(values: string[]): string | null {
  const normalizedStates = normalizeStateSelections(values);
  if (normalizedStates.length === 0) {
    return 'Enter at least one licensed state.';
  }

  const invalidStates = normalizedStates.filter((state) => !isUsStateCode(state));
  if (invalidStates.length > 0) {
    return `Use 2-letter US state codes (invalid: ${invalidStates.join(', ')}).`;
  }

  return null;
}

function validateLicensedPracticeAreas(value: string): string | null {
  const normalizedPracticeAreas = normalizeCommaSeparatedList(value);
  if (normalizedPracticeAreas.length === 0) {
    return 'Enter at least one licensed practice area.';
  }

  const invalidPracticeAreas = normalizedPracticeAreas.filter(
    (practiceArea) => toSupportedPracticeArea(practiceArea) === null
  );
  if (invalidPracticeAreas.length > 0) {
    return `Use supported practice areas only (${SUPPORTED_PRACTICE_AREAS.join(', ')}).`;
  }

  return null;
}

export function validateFillEmployeeRecordForm(values: {
  displayName: string;
  email: string;
  licensedStates: string[];
  licensedPracticeAreas: string;
}): FillEmployeeRecordFormErrors {
  return {
    displayName: validateDisplayName(values.displayName),
    email: validateEmail(values.email),
    licensedStates: validateLicensedStates(values.licensedStates),
    licensedPracticeAreas: validateLicensedPracticeAreas(values.licensedPracticeAreas),
  };
}

export function normalizeValidatedPracticeAreas(value: string): string[] {
  const normalizedPracticeAreas = normalizeCommaSeparatedList(value);
  return normalizedPracticeAreas.flatMap((practiceArea) => {
    const supportedPracticeArea = toSupportedPracticeArea(practiceArea);
    return supportedPracticeArea === null ? [] : [supportedPracticeArea];
  });
}
